import {
  Button,
  CircularProgress,
  DialogActions,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import LabelledDropdown from 'components/atoms/Dropdown/LabelledDropdown';
import { filter, includes, isEmpty, join, map, reduce } from 'lodash';
import React, { ReactElement, useMemo, useState } from 'react';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { Action, StainsActions } from './presetService';

export interface PresetStainModalProps {
  currentStains: string[];
  presetStains: string[];
  onFinish: (stainsActions: StainsActions) => void;
}

const allActions = [Action.KEEP, Action.DELETE, Action.REASSIGN];
const optionsWithoutReassign = [Action.KEEP, Action.DELETE];

const PresetStainModal = ({ currentStains, presetStains, onFinish }: PresetStainModalProps): ReactElement => {
  const { stainTypeIdToDisplayName, isLoadingStainTypeOptions } = useStainTypeIdToDisplayName();

  const { stainsInPresetNotInCurrent, defaultStainsActions } = useMemo(() => {
    const newStainsInPresetNotInCurrent = filter(presetStains, (stain) => !includes(currentStains, stain));
    return {
      stainsInPresetNotInCurrent: newStainsInPresetNotInCurrent,
      defaultStainsActions: reduce(
        newStainsInPresetNotInCurrent,
        (acc, stain) => {
          acc[stain] = { action: isEmpty(currentStains) ? Action.KEEP : Action.DELETE };
          return acc;
        },
        {} as StainsActions
      ),
    };
  }, [presetStains, currentStains]);

  const [stainsActionsState, setStainsActions] = useState<StainsActions>(undefined);

  const stainsActions = stainsActionsState || defaultStainsActions;

  const actionOptions = isEmpty(currentStains) ? optionsWithoutReassign : allActions;

  return (
    <Dialog
      open={true}
      onClick={(e) => e.stopPropagation()}
      aria-labelledby="responsive-dialog-title"
      data-testid="confirmation-modal"
      data-cy="confirmation-modal"
    >
      <DialogTitle id="responsive-dialog-title">Select Action For Stains In Preset</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Typography>
            {isLoadingStainTypeOptions ? (
              <>
                {'Current Stains: '}
                <CircularProgress />
              </>
            ) : !isEmpty(currentStains) ? (
              <>
                {'Current Stains: '}
                {join(
                  map(currentStains, (stain) => stainTypeIdToDisplayName(stain)),
                  ', '
                )}
              </>
            ) : (
              'Please make sure the preset stains are compatible with your intended configuration.'
            )}
          </Typography>
          <Typography>
            {'Preset Stains: '}
            {isLoadingStainTypeOptions ? (
              <CircularProgress />
            ) : (
              join(
                map(presetStains, (stain) => stainTypeIdToDisplayName(stain)),
                ', '
              )
            )}
          </Typography>
          {!isEmpty(currentStains) && (
            <Typography variant="h4">
              {'The mismatched stains: '}
              {isLoadingStainTypeOptions ? (
                <CircularProgress />
              ) : (
                join(
                  map(stainsInPresetNotInCurrent, (stain) => stainTypeIdToDisplayName(stain)),
                  ', '
                )
              )}
            </Typography>
          )}
        </DialogContentText>
        {
          <DialogContentText>
            <Typography variant="h4" mt={2}>
              Choose action for each stain:
            </Typography>

            <Grid container spacing={2} p={2}>
              {map(stainsInPresetNotInCurrent, (stain) => (
                <Grid container spacing={1} alignItems="center" key={stain}>
                  <Grid item xs={3}>
                    {isLoadingStainTypeOptions ? (
                      <CircularProgress />
                    ) : (
                      <Typography> {stainTypeIdToDisplayName(stain)}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <RadioGroup
                      row
                      value={stainsActions[stain]?.action}
                      onChange={(event) => {
                        setStainsActions((prev) => ({
                          ...prev,
                          [stain]: {
                            action: event.target.value as Action,
                          },
                        }));
                      }}
                    >
                      {map(actionOptions, (action) => (
                        <FormControlLabel key={action} value={action} control={<Radio />} label={action} />
                      ))}
                    </RadioGroup>
                  </Grid>
                  {stainsActions[stain]?.action === Action.REASSIGN && (
                    <Grid item xs={2}>
                      <LabelledDropdown
                        size="small"
                        label="Stain Type"
                        options={map(currentStains, (currentStain) => ({
                          value: currentStain,
                          text: stainTypeIdToDisplayName(currentStain),
                        }))}
                        loading={isLoadingStainTypeOptions}
                        value={stainsActions[stain]?.reassignTo || ''}
                        onOptionSelected={(optionValue) => {
                          setStainsActions((prev) => ({
                            ...prev,
                            [stain]: {
                              action: Action.REASSIGN,
                              reassignTo: optionValue,
                            },
                          }));
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              ))}
            </Grid>
          </DialogContentText>
        }
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => onFinish(stainsActions)}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PresetStainModal;
